/**
 * Overdue + AR Aging Card
 *
 * Replaces the simple "Overdue" metric with a card that also breaks the
 * outstanding overdue amount into 1-30 / 31-60 / 61-90 / 90+ day buckets.
 * The aging story is the difference between $X being recoverable and being
 * a write-off candidate, so we surface it on the dashboard rather than
 * burying it in a report.
 */

import React from "react";
import { Box, CardContent, Typography, Tooltip } from "@mui/material";
import { Warning as OverdueIcon } from "@mui/icons-material";
import type { AgingBucket, AgingBucketId } from "@/types/admin";
import { StatCardFrame, StatCardHead } from "./StatCard";

interface OverdueAgingCardProps {
  amount: number;
  invoiceCount: number;
  buckets?: AgingBucket[];
  onClick?: () => void;
  /** Called when a user clicks a specific aging bucket segment. Use this to
   * drill down to `/invoices?aged=<id>`. Falls back to the card-level
   * `onClick` if not provided. */
  onBucketClick?: (bucket: AgingBucketId) => void;
}

interface BucketStyle {
  label: string;
  shortLabel: string;
  color: string;
}

const BUCKET_STYLES: Record<AgingBucketId, BucketStyle> = {
  "1_30": { label: "1–30 days", shortLabel: "1–30", color: "#FBC02D" },
  "31_60": { label: "31–60 days", shortLabel: "31–60", color: "#FB8C00" },
  "61_90": { label: "61–90 days", shortLabel: "61–90", color: "#E64A19" },
  "90_plus": { label: "90+ days", shortLabel: "90+", color: "#C62828" },
};

const ORDERED: AgingBucketId[] = ["1_30", "31_60", "61_90", "90_plus"];

const formatCurrency = (n: number) =>
  `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const OverdueAgingCard: React.FC<OverdueAgingCardProps> = ({
  amount,
  invoiceCount,
  buckets,
  onClick,
  onBucketClick,
}) => {
  const safeBuckets: AgingBucket[] = ORDERED.map((id) => {
    const found = buckets?.find((b) => b.bucket === id);
    return {
      bucket: id,
      count: found?.count ?? 0,
      amount: found ? Number(found.amount) : 0,
    };
  });

  const total = safeBuckets.reduce((sum, b) => sum + b.amount, 0);
  const hasAging = total > 0;

  return (
    <StatCardFrame onClick={onClick}>
      <CardContent
        sx={{
          p: 2.25,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          "&:last-child": { pb: 2.25 },
        }}
      >
        <StatCardHead
          label="Overdue"
          tooltip="Unpaid invoices past their due date. The bar below splits the total into 1–30 / 31–60 / 61–90 / 90+ day aging buckets — click a segment to drill in."
          icon={<OverdueIcon />}
          color="error"
          value={formatCurrency(amount)}
          subtitle={`${invoiceCount} ${invoiceCount === 1 ? "invoice" : "invoices"} · as of today`}
        />

        {/* Aging stacked bar */}
        <Box sx={{ mt: 1.5 }}>
          {hasAging ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  height: 10,
                  borderRadius: 5,
                  overflow: "hidden",
                  bgcolor: "grey.100",
                }}
              >
                {safeBuckets.map((b) => {
                  const pct = total > 0 ? (b.amount / total) * 100 : 0;
                  if (pct <= 0) return null;
                  const style = BUCKET_STYLES[b.bucket as AgingBucketId];
                  const clickable = !!onBucketClick && b.count > 0;
                  const handleClick = clickable
                    ? (e: React.MouseEvent) => {
                        e.stopPropagation();
                        onBucketClick(b.bucket as AgingBucketId);
                      }
                    : undefined;
                  return (
                    <Tooltip
                      key={b.bucket}
                      arrow
                      placement="top"
                      title={
                        <Box sx={{ p: 0.25 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
                            {style.label}
                          </Typography>
                          <Typography variant="caption" sx={{ display: "block" }}>
                            {formatCurrency(b.amount)} · {b.count} {b.count === 1 ? "invoice" : "invoices"}
                          </Typography>
                          {clickable && (
                            <Typography variant="caption" sx={{ display: "block", opacity: 0.7, mt: 0.25 }}>
                              Click to view
                            </Typography>
                          )}
                        </Box>
                      }
                    >
                      <Box
                        role={clickable ? "button" : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        aria-label={clickable ? `View ${style.label} invoices` : undefined}
                        onClick={handleClick}
                        onKeyDown={
                          clickable
                            ? (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onBucketClick(b.bucket as AgingBucketId);
                                }
                              }
                            : undefined
                        }
                        sx={{
                          width: `${pct}%`,
                          bgcolor: style.color,
                          transition: "width 0.3s, filter 0.15s",
                          cursor: clickable ? "pointer" : "default",
                          "&:hover": clickable ? { filter: "brightness(1.15)" } : {},
                          "&:focus-visible": clickable
                            ? { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 1 }
                            : {},
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
              {/*
                Wrap, because the labels below refuse to break. Without this
                the four buckets overflow the Card — which is overflow:hidden,
                so they are CLIPPED, not scrollable: at 375px the "90+" bucket
                and its drill-in button disappeared entirely.
              */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  mt: 0.5,
                  gap: 0.5,
                }}
              >
                {safeBuckets.map((b) => {
                  const style = BUCKET_STYLES[b.bucket as AgingBucketId];
                  const clickable = !!onBucketClick && b.count > 0;
                  const dim = b.count === 0;
                  return (
                    <Box
                      key={b.bucket}
                      role={clickable ? "button" : undefined}
                      tabIndex={clickable ? 0 : undefined}
                      // Leads with the visible "1–30 · 12" so the name
                      // contains the text a voice user would say (WCAG 2.5.3).
                      aria-label={
                        clickable
                          ? `${style.shortLabel} · ${b.count} — view ${style.label} overdue invoices`
                          : undefined
                      }
                      onClick={
                        clickable
                          ? (e) => {
                              e.stopPropagation();
                              onBucketClick(b.bucket as AgingBucketId);
                            }
                          : undefined
                      }
                      onKeyDown={
                        clickable
                          ? (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                e.stopPropagation();
                                onBucketClick(b.bucket as AgingBucketId);
                              }
                            }
                          : undefined
                      }
                      sx={{
                        // NOT flex: 1. With the row wrapping, a grown item
                        // fills its whole line — at 1280 the 90+ bucket ended
                        // up alone on line two, stretched to 188px and
                        // centred under nothing.
                        flex: "0 1 auto",
                        textAlign: "center",
                        // "1-30" must not break across lines into "1-" / "30",
                        // which is what these buckets did at 375px.
                        whiteSpace: "nowrap",
                        opacity: dim ? 0.4 : 1,
                        cursor: clickable ? "pointer" : "default",
                        py: 0.25,
                        borderRadius: 0.5,
                        transition: "background-color 0.15s",
                        "&:hover": clickable ? { bgcolor: "action.hover" } : {},
                        "&:focus-visible": clickable
                          ? { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 1 }
                          : {},
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-block",
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: style.color,
                          mr: 0.5,
                          verticalAlign: "middle",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.6rem", color: "text.secondary", verticalAlign: "middle" }}
                      >
                        {style.shortLabel}
                        {b.count > 0 ? ` · ${b.count}` : ""}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </>
          ) : (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.65rem"
              }}>
              No aging breakdown
            </Typography>
          )}
        </Box>
      </CardContent>
    </StatCardFrame>
  );
};
